// One order, on a sheet that can be printed and put in the parcel.
//
// The sheet is A4: what it holds is the order as the buyer should read it —
// who it goes to, what is in it, what it cost. Everything that changes the
// order (ship it, complete it, call it off, give money back) sits around the
// sheet and is left out of the print, which is what `print:hidden` says.
import React, { useEffect, useMemo, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Button from "aldoo-ra/Button"
import Spinner from "aldoo-ra/Spinner"
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import RefundDialog from "aldoo-ra/Admin/Store/Refunds/RefundDialog"
import ReturnDialog, { REASON_LABELS } from "./ReturnDialog"
import ModifyDialog from "./ModifyDialog"
import MoveDialog from "./MoveDialog"
import RequestThread from "./RequestThread"
import OrderTimeline from "./OrderTimeline"
import { TrackingForm, TrackingPanel, trackingNumberProblem } from "./Tracking"
import { AcceptReturnAddress } from "./ReturnAddress"
import { url as cmsUrl } from "aldoo-ra/CMS/url"
import PrintSections from "./PrintSections"
import InvoicePanel from "./InvoicePanel"
import ClientPanel, { ClientFlag } from "./ClientPanel"
import { describeLine } from "aldoo-ra/Store/lines"
import { PrinterIcon, ChevronUpIcon, CalendarDaysIcon, MapPinIcon, TruckIcon, ArrowUturnLeftIcon, CheckCircleIcon, XCircleIcon, ReceiptRefundIcon, DocumentDuplicateIcon, ArrowUturnDownIcon, BanknotesIcon, DocumentArrowUpIcon, ExclamationTriangleIcon, PencilSquareIcon, ArrowsRightLeftIcon } from "@heroicons/react/24/outline"
import { Chip, Empty, Thumb, money, dateTime, dateOnly, timeAgo, placedAt, errorMessage, orderState, presaleOf, awaitingShipment, PRESALE, CANCEL_REASON_LABELS } from "./ui"
import useStoreEvents from "aldoo-ra/Store/useStoreEvents"
import useOrderTexts from "./useOrderTexts"
import { storeOrdersAPI } from "./api"
import { useUrlParam } from "aldoo-ra/URLState"
import HelpHint from "aldoo-ra/HelpCenter/InPlaceHelp"


/**
 * One action of the order, as the menu shows it: what it is called, and what
 * it does to the order — an order is money and a promise, and "Cancel" next
 * to "Refund" is worth a sentence.
 */
// what each action does, at a glance
const ACTION_ICONS = { move: ArrowsRightLeftIcon, modify: PencilSquareIcon, ship: TruckIcon, reopen: ArrowUturnLeftIcon, complete: CheckCircleIcon, cancel: XCircleIcon, returned: ArrowUturnDownIcon, refund: ReceiptRefundIcon, attach_invoice: DocumentArrowUpIcon }
const ACTION_TONES = { cancel: "text-admin_error", returned: "text-admin_warning", refund: "text-admin_warning", complete: "text-admin_success" }

/** What the buyer chose on a line ("Size: M") and, for an outfit, each part to pack ("2 × T-shirt: Size: M · TS-M"). */
const choiceText = (labels) => Object.values(labels || {}).map((entry) => (entry?.label ? `${entry.label}: ${entry.value ?? ""}` : String(entry?.value ?? ""))).filter(Boolean).join(", ")
const lineDetails = (item) => [
  choiceText(item.choiceLabels),
  ...(item.components || []).map((part) => {
    const chosen = choiceText(part.choiceLabels)
    const name = `${(part.qty || 1) * (item.quantity || 1)} × ${part.label || part.pid}`
    return [chosen ? `${name}: ${chosen}` : name, part.sku || part.pid].join(" · ")
  }),
].filter(Boolean)

const ActionOption = ({ option }) => {
  const Icon = option.icon || ACTION_ICONS[option.value]
  return (
    <div className="flex items-start gap-2.5 py-0.5">
      {Icon && <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${ACTION_TONES[option.value] || "text-admin_primary"}`} />}
      <div className="flex flex-col">
        <span className="text-sm font-medium text-admin_text">{option.name}</span>
        <span className="text-xs text-admin_text_secondary">{option.hint}</span>
      </div>
    </div>
  )
}

const address = (value) =>
  [value?.name, value?.street || value?.line1, value?.line2, [value?.postalCode || value?.postal_code, value?.city].filter(Boolean).join(" "), value?.state, value?.country]
    .filter(Boolean)
    .join("\n")

/** The pickup office as far as the order says it: its name and address. */
const DefaultPickup = ({ office }) => (
  <>
    <div className="font-medium">{office.name}</div>
    {office.address && <div className="text-black/70">{typeof office.address === "string" ? office.address : [office.address.line1 || office.address.street, office.address.city].filter(Boolean).join(", ")}</div>}
    {office.id && <div className="text-black/70 font-mono">{office.id}</div>}
  </>
)

// `renderShip({ order, t, locale, onClose, onShipped, onManual })`: the project's dialog that makes the
// waybill with the courier (the Ship action opens it instead of the tracking-number form; `onManual`
// falls back to that form); `renderShipment({ order, t, locale, onChanged })`: the waybill made, on the
// Delivery tab (its label, its cancellation)
// `extraPrints({ order, t })`: what else the project prints of an order besides the order itself (its
// waybill's label, A6 / A4) — `[{ value, name, hint?, icon?, run }]`; none: Print prints the order
/** A tab's name and its count in a chip ("Items 12"). */
const TabCount = ({ label, count, testId }) => (
  <span className="inline-flex items-center gap-1.5">
    {label}
    <span className="inline-flex items-center justify-center min-w-[22px] h-[20px] px-1.5 rounded-full bg-black/[0.07] text-[12px] font-semibold tabular-nums text-black/70" data-testid={testId}>
      {count}
    </span>
  </span>
)

// `helpKeys`: the help center's articles behind the "?" hints (StoreOrders)
export default function OrderPage({ orgId = null, orderId, onClose, onChanged, onOpenOrder, api = storeOrdersAPI, useTexts = useOrderTexts, locale = "en-GB", renderPickup, renderQR, renderShip, renderShipment, renderOfficePicker, extraPrints, helpKeys = {}, viewer = "admin" }) {
  const t = useTexts()
  // the seller's own screen (the vendor portal): what an Aldoo admin did with the order is told as the
  // seller is told, even to an admin looking in — where a moved order went is the admins' screens' alone
  const vendorView = viewer === "vendor"
  const [shipping, setShipping] = useState(false)
  const [order, setOrder] = useState(null)
  const [error, setError] = useState("")
  const [busy, setBusy] = useState(false)
  const [refunding, setRefunding] = useState(false)
  // the refund opens at the order dialog's size, so it takes its place instead of jumping
  const [refundHeight, setRefundHeight] = useState(null)
  // the units picked when the refund opens: the goods that just came back (by cart index)
  const [refundInitial, setRefundInitial] = useState(null)
  const [returning, setReturning] = useState(false)
  // the return dialog started from a buyer's request: its reason, units and id
  const [returnPreset, setReturnPreset] = useState(null)
  // the order's items being changed before it is sent (ModifyDialog)
  const [modifying, setModifying] = useState(false)
  // an Aldoo admin moving it to another seller (MoveDialog)
  const [moving, setMoving] = useState(false)
  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(id)
      showNotification({ message: t`Order ID copied`, className: "bg-admin_success text-white" })
    } catch {
      showNotification({ message: t`Could not copy`, className: "bg-admin_error text-white" })
    }
  }
  const panelHeight = () => {
    const panel = typeof document !== "undefined" ? document.querySelector(".order-dialog-panel") : null
    return panel ? panel.getBoundingClientRect().height : null
  }
  const openRefund = (initial = null) => {
    setRefundHeight(panelHeight())
    setRefundInitial(initial)
    setRefunding(true)
  }
  const openReturn = () => {
    setRefundHeight(panelHeight())
    // the goods of accepted requests arriving: the return starts from all of them (their items
    // together, the first one's reason) and taking them in fulfils each
    const all = order?.returned?.requests || []
    const awaiting = all.map((request, index) => ({ ...request, number: all.length - index })).filter((request) => request.status === "accepted" && !request.returnedAt)
    setReturnPreset(awaiting.length ? { requests: awaiting } : null)
    setReturning(true)
  }

  // the seller's answer to a buyer's return request, with the message the buyer reads
  const answer = (request, decision) => {
    // the address the buyer is sent; added from the dialog itself when there was none
    let returnAddress = order?.returned?.returnAddress || ""
    let message = decision === "accepted" ? t`Thank you for your request. Please send the items back to us — we will refund your order as soon as we receive the delivery.` : ""
    showPromptDialog({
      variant: "admin",
      title: decision === "accepted" ? t`Accept the return request` : t`Decline the return request`,
      message: (
        <div className="flex flex-col gap-3">
          <span>{decision === "accepted" ? t`The buyer is told by mail, with your message. When the parcel arrives, mark it as returned from the request.` : t`The buyer is told by mail, with your reason.`}</span>
          {/* accepting sends the buyer the seller's return address (Delivery settings): shown, or added on the spot */}
          {decision === "accepted" && order?.returned && (
            <AcceptReturnAddress order={order} api={api} t={t} initial={order.returned.returnAddress || ""} onChange={(value) => (returnAddress = value)} />
          )}
          <label className="flex flex-col gap-1">
            <span className="text-xs text-admin_text_secondary">{decision === "accepted" ? t`Message to the buyer` : t`Why the request is declined (required)`}</span>
            <textarea
              rows={4}
              defaultValue={message}
              onChange={(e) => (message = e.target.value)}
              className="px-3 py-2 rounded-md border text-sm outline-none resize-y border-admin_bordered bg-admin_paper text-admin_text"
              data-testid="request-message"
            />
          </label>
        </div>
      ),
      // the message goes to the buyer: the dialog sends it or closes, whichever the decision
      confirmText: t`Send`,
      cancelText: t`Close`,
      onConfirm: () => {
        if (decision === "declined" && !String(message).trim()) {
          showNotification({ message: t`Tell the buyer why the request is declined`, className: "bg-admin_error text-white" })
          return false
        }
        if (decision === "accepted" && order?.returned && !returnAddress) {
          showNotification({ message: t`Add your return address in your Delivery settings first`, className: "bg-admin_error text-white" })
          return false
        }
        act(() => api.resolveRequest(id, { request_id: request._id, decision, message }), decision === "accepted" ? t`Request accepted, the buyer was told` : t`Request declined, the buyer was told`)
      },
    })
  }

  // the seller's answer to a cancellation request: accepted refunds what is left and cancels the order
  const answerCancel = (request, decision) => {
    // a card payment is given back on accepting; an order to be paid on delivery has nothing to give back
    const left = Math.max(0, (order.amount || 0) - (order.refundedAmount || 0))
    const refunds = order.paymentMethod?.type !== "cash_on_delivery" && left > 0
    let message = decision === "accepted" ? t`Thank you for letting us know in time.` : ""
    showPromptDialog({
      variant: "admin",
      title: decision !== "accepted" ? t`Decline the cancellation` : refunds ? t`Cancel the order and refund` : t`Cancel the order`,
      message: (
        <div className="flex flex-col gap-3">
          <span>
            {decision !== "accepted"
              ? t`The order stays and will be sent. The buyer is told by mail, with your reason.`
              : refunds
                ? t("%s is canceled and what is left of the payment (%s) goes back to the buyer. They are told by mail, with your message.", id, money(left, currency))
                : t("%s is canceled. Nothing was paid, so no money goes back. The buyer is told by mail, with your message.", id)}
          </span>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-admin_text_secondary">{decision === "accepted" ? t`Message to the buyer` : t`Why the cancellation is declined (required)`}</span>
            <textarea
              rows={4}
              defaultValue={message}
              onChange={(e) => (message = e.target.value)}
              className="px-3 py-2 rounded-md border text-sm outline-none resize-y border-admin_bordered bg-admin_paper text-admin_text"
              data-testid="cancel-request-message"
            />
          </label>
        </div>
      ),
      confirmText: decision === "accepted" ? t`Proceed` : t`Send`,
      cancelText: t`Close`,
      onConfirm: () => {
        if (decision === "declined" && !String(message).trim()) {
          showNotification({ message: t`Tell the buyer why the cancellation is declined`, className: "bg-admin_error text-white" })
          return false
        }
        if (!api.resolveCancel) return
        act(() => api.resolveCancel(id, { request_id: request._id, decision, message }), decision !== "accepted" ? t`Cancellation declined, the buyer was told` : refunds ? t`Order canceled and refunded, the buyer was told` : t`Order canceled, the buyer was told`)
      },
    })
  }

  const load = () =>
    api.get(orderId).then((res) => {
      const message = errorMessage(res)
      if (message || !res?.order) return setError(message || t`Order not found`)
      setOrder(res.order)
    })

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId])

  // the order changed elsewhere (realtime): it is read again; what the buyer did to it under the
  // seller's eyes — a request asked or taken back — is said, so the change is not a surprise
  const BUYER_CHANGES = {
    cancel_requested: t`The buyer just asked to cancel this order`,
    cancel_withdrawn: t`The buyer withdrew their cancellation request`,
    return_requested: t`The buyer just asked to return items of this order`,
    return_withdrawn: t`The buyer withdrew their return request`,
  }
  useStoreEvents({
    orgId: orgId || null,
    events: ["store:order"],
    onEvent: (notice) => {
      const mine = notice?.orderId && [String(orderId), String(order?.orderId || ""), String(order?._id || "")].includes(String(notice.orderId))
      if (!mine) return
      load()
      if (notice.by === "buyer" && BUYER_CHANGES[notice.change]) {
        showNotification({ message: BUYER_CHANGES[notice.change], className: "bg-admin_warning text-white" })
      }
    },
  })

  const id = order?.orderId || orderId
  const currency = order?.currency

  const act = async (fn, ok) => {
    setBusy(true)
    try {
      const res = await fn()
      const message = errorMessage(res)
      showNotification({ message: message || ok, className: message ? "bg-admin_error text-white" : "bg-admin_success text-white" })
      if (message) return false
      await load()
      onChanged?.()
      return true
    } finally {
      setBusy(false)
    }
  }

  // shipping asks for the parcel's tracking number: the buyer follows it, and the order
  // completes once the courier says it was delivered
  const parcelForm = ({ title, intro, confirmText, initial, send, done }) => {
    let tracking = { carrier: initial?.carrier || order?.shippingProvider?.providerId || "other", number: initial?.number || "", url: initial?.url || "" }
    showPromptDialog({
      variant: "admin",
      title,
      message: (
        <div className="flex flex-col gap-3">
          {intro && <span>{intro}</span>}
          <TrackingForm order={order} t={t} initial={initial} onChange={(value) => (tracking = value)} />
        </div>
      ),
      confirmText,
      cancelText: t`Cancel`,
      onConfirm: () => {
        if (!String(tracking.number || "").trim()) {
          showNotification({ message: t`Enter the parcel's tracking number`, className: "bg-admin_error text-white" })
          return false
        }
        // a number that cannot be the courier's (the server turns it back too)
        const problem = trackingNumberProblem(order, tracking)
        if (problem) {
          showNotification({ message: t(problem), className: "bg-admin_error text-white" })
          return false
        }
        act(() => send(tracking), done)
      },
    })
  }

  // collected at the seller's own shop (shipping provider "shop"): no parcel to send — it is made ready, and the buyer told
  const atShop = order?.shippingProvider?.providerId === "shop"
  // paid on delivery there: "pay at the shop" (the order's mark, or its shop delivery for an order from before the mark)
  const payAtShop = order?.paymentMethod?.type === "cash_on_delivery" && (order.paymentMethod.place === "shop" || atShop)
  const readyForPickup = () =>
    showPromptDialog({
      variant: "admin",
      title: t`Ready for pickup`,
      message: openCancel
        ? t("The buyer asked to cancel %s. Marking it ready declines their request and tells them. Continue?", id)
        : t("Mark %s as ready for pickup and email %s that it is waiting at your shop.", id, order.email),
      confirmText: t`Ready for pickup`,
      cancelText: t`Cancel`,
      onConfirm: () => act(() => api.ship(id, null), t`Marked as ready for pickup`),
    })
  const ship = () => (atShop ? readyForPickup() : renderShip ? setShipping(true) : shipByNumber())

  const shipByNumber = () =>
    parcelForm({
      title: t`Ship this order`,
      intro: openCancel
        ? t("The buyer asked to cancel %s. Shipping it declines their request and tells them. Ship anyway?", id)
        : t("Mark %s as shipping and send the shipping mail, with the tracking number, to %s.", id, order.email),
      confirmText: t`Ship`,
      send: (tracking) => api.ship(id, tracking),
      done: t`Marked as shipping, the buyer was told`,
    })

  const editTracking = () =>
    parcelForm({
      title: order?.tracking?.number ? t`Change the tracking number` : t`Add the tracking number`,
      confirmText: t`Save`,
      initial: order?.tracking,
      send: (tracking) => (api.setTracking ? api.setTracking(id, tracking) : Promise.resolve({ error: "unsupported" })),
      done: t`Tracking number saved`,
    })

  const reopen = () =>
    showPromptDialog({
      variant: "admin",
      title: t`Reopen this order`,
      message: t("%s goes back to open — waiting to be sent. Nothing is mailed to the buyer.", id),
      confirmText: t`Reopen`,
      cancelText: t`Leave it`,
      onConfirm: () => act(() => api.setStatus(id, "paid"), t`Order reopened`),
    })

  const complete = () =>
    showPromptDialog({
      variant: "admin",
      title: t`Complete this order`,
      message: t("%s is done — the buyer has it. Nothing is mailed to the buyer.", id),
      confirmText: t`Complete`,
      cancelText: t`Leave it`,
      // paid on delivery: the buyer has paid, so on to the Invoice tab — its invoice is next
      onConfirm: async () => {
        if ((await act(() => api.setStatus(id, "completed"), t`Order completed`)) && invoiceTab) setSection("invoice")
      },
    })

  // the buyer is mailed that the order is off, with the seller's words when they write any
  const cancel = () => {
    let message = ""
    showPromptDialog({
      variant: "admin",
      title: t`Cancel this order`,
      message: (
        <div className="flex flex-col gap-3">
          <span>{t("%s is called off and the buyer is told by email. Any money already taken is given back separately, with Refund.", id)}</span>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-admin_text_secondary">{t`Message to the buyer (optional)`}</span>
            <textarea
              rows={3}
              onChange={(e) => (message = e.target.value)}
              placeholder={t`For example: the item is out of stock`}
              className="px-3 py-2 rounded-md border text-sm outline-none resize-y border-admin_bordered bg-admin_paper text-admin_text"
              data-testid="cancel-order-message"
            />
          </label>
        </div>
      ),
      confirmText: t`Cancel the order`,
      cancelText: t`Keep it`,
      onConfirm: () => act(() => api.setStatus(id, "canceled", { message }), t`Order canceled, the buyer was told`),
    })
  }

  // the order as it stands after its refunds, as the server reads it
  // (aldoo-na/store/refunds netOf): the lines left, each refund, the money
  // net of them. An order from a server that does not say is its cart.
  const final = useMemo(() => {
    if (order?.final) return order.final
    const cart = (order?.cart || []).map((item, cartIndex) => ({ ...item, cartIndex, ordered: item.quantity ?? 1, refundedQuantity: 0 }))
    const refunded = order?.refundedAmount ?? 0
    return { lines: cart, refunds: [], totals: { refunded, net: Math.max(0, (order?.amount ?? 0) - refunded) }, state: refunded > 0 ? "partial" : "none" }
  }, [order])

  const totals = useMemo(() => {
    const lines = order?.cart || []
    // the lines as they were bought: the refunds are said below them, one row each
    const items = lines.reduce((sum, item) => sum + (item.price ?? 0) * (item.quantity ?? 1), 0)
    // what the lines would have cost at their full price: the difference is
    // the discount the buyer was given, per line
    const full = lines.reduce((sum, item) => sum + ((item.originalPrice ?? item.price) ?? 0) * (item.quantity ?? 1), 0)
    return {
      items,
      full,
      lineDiscount: Math.max(0, full - items),
      // a promo code is taken off the order as a whole, beside the line prices
      promoDiscount: order?.discountAmount ?? 0,
      shipping: order?.shippingCost ?? 0,
      refunded: final.totals.refunded ?? order?.refundedAmount ?? 0,
      net: final.totals.net ?? Math.max(0, (order?.amount ?? 0) - (order?.refundedAmount ?? 0)),
    }
  }, [order, final])

  /** What a refund was, in a word: the whole of what was left, some of the goods, or a sum of money. */
  const refundKind = (refund) => {
    if (refund.mode === "full") return t`Full amount`
    if (refund.lines?.length) return t`Selected items`
    return t`Amount`
  }

  // a card order can be given back; one paid in coins or through a store has
  // no charge of ours to reverse. One paid on delivery, once paid (completed), is
  // given back by the seller themselves — recorded here, so the sales take it off
  const paidOnDelivery = order?.paymentMethod?.type === "cash_on_delivery" && order?.status === "completed"
  const refundable = (!!order?.paymentIntentId || paidOnDelivery) && (order?.amount ?? 0) - (order?.refundedAmount ?? 0) > 0

  // the order's contacts, as the API resolves them (the account name only when
  // the buyer gave one), falling back to what the order itself carries
  const customer = order?.customer || {
    email: order?.email || null,
    name: null,
    // the phone rides with the address: the one address of a new order, or an older order's two
    phone: order?.address?.phone || order?.shippingAddress?.phone || order?.billingAddress?.phone || null,
  }

  // the order's one address. Older orders kept it twice — and the delivery
  // copy often without its street — so the fullest of what is there is used.
  const has = (value) => !!(value && (value.line1 || value.street || value.name))
  const addresses = [order?.address, order?.shippingAddress, order?.billingAddress].filter(has)
  const orderAddress = addresses.find((value) => value.line1 || value.street) || addresses[0] || null

  // how it travels, as the delivery step put it to the buyer: the courier and
  // the way — "Speedy · Delivery to address". Older orders recorded neither.
  const METHODS = {
    address: "Delivery to address",
    office: "Pickup at office",
    locker: "Pickup from locker",
    delivery_to_address: "Delivery to address",
    pickup_from_office: "Pickup from office",
    pickup: "Pickup from the shop",
  }
  const provider = order?.shippingProvider
  // the seller's own shop is no carrier: the way alone
  const carrier = provider?.providerId === "shop" ? t`Pickup from the shop` : [provider?.providerName || provider?.providerId, METHODS[provider?.method] && t(METHODS[provider?.method])].filter(Boolean).join(" · ") || null
  // a parcel collected at an office or a locker: where it is sent
  const pickupOffice = provider?.office || null
  // the order in two tabs on screen — how it travels, what is in it; printed, both
  // a line that says it travels, or — lines that say nothing — an order that was given a way to ship
  const shipsSomething = (order?.cart || []).some((item) => item.isPhysical === true || (item.isPhysical === undefined && !!order?.shippingProvider))
  // the open tab is in the url (?order_tab=), so a link or a refresh lands on it
  const [section, setSection] = useUrlParam("order_tab", { default: "" })
  // a buyer's request waiting for an answer is what the seller opened the order for
  const requests = order?.returned?.requests || []
  // the order line a request or a return names, for its picture and what was chosen on it
  const cartLineOf = (line) => (order?.cart || []).find((item) => (line.lineKey ? item.lineKey === line.lineKey : item.pid === line.pid)) || {}
  const openRequests = requests.filter((request) => request.status === "open").length
  // the buyer asks to call the order off before it is sent: answered before anything else
  const openCancel = (order?.cancelRequests || []).find((request) => request.status === "open") || null
  // the tabs this order has; one named in the url that it has not (another order's) is let go
  // the Delivery tab's own parts: the address always, the waybill made here (the project's), the tracking
  // once the order is on its way
  const [deliveryTab, setDeliveryTab] = useState("address")
  const hasShipment = !!renderShipment && !!order?.shipment?.number && !order.shipment.canceledAt
  const deliveryTabs = [
    { id: "address", label: t`Address` },
    ...(hasShipment ? [{ id: "waybill", label: t`Waybill` }] : []),
    ...(order?.tracking?.number || ["shipping", "completed"].includes(order?.orderStatus) ? [{ id: "tracking", label: t`Tracking` }] : []),
  ]
  const deliveryShown = deliveryTabs.some((tab) => tab.id === deliveryTab) ? deliveryTab : "address"
  // a part gone (the waybill canceled: no waybill, no tracking any more): back to the address, so a waybill
  // made again does not open on a part chosen before
  useEffect(() => {
    if (deliveryTab !== deliveryShown) setDeliveryTab(deliveryShown)
  }, [deliveryTab, deliveryShown])
  // what Print offers: the order itself, then what the project adds (its waybill's label)
  const prints = order ? [{ value: "print_order", name: t`Order`, hint: t`The order's sheet`, icon: PrinterIcon, run: () => window.print() }, ...((extraPrints && extraPrints({ order, t })) || [])] : []
  // the History tab: the order's events and its courier's, in one line of time (as the order's history runs)
  const courierName = order?.tracking?.carrier && order?.shippingProvider?.providerId === order.tracking.carrier ? order.shippingProvider.providerName || order.tracking.carrier : order?.tracking?.carrier
  const historyWithCourier = (() => {
    const own = order?.history || []
    const courier = (order?.tracking?.events || []).filter((event) => event?.at).map((event) => ({ type: "courier", at: event.at, text: event.text, place: event.place, state: event.state, carrier: courierName }))
    if (!courier.length) return own
    const newestFirst = own.length > 1 && new Date(own[0].at) > new Date(own[own.length - 1].at)
    const all = [...own, ...courier]
    // a courier's event never reads before the waybill (or shipping) it is about: its clock may be a few
    // seconds ahead of ours (as the server's history orders them: aldoo-na store/order-history)
    const anchors = all.filter((event) => ["waybill_created", "shipped", "tracking_set"].includes(event.type)).map((event) => new Date(event.at).getTime())
    const when = new Map(all.map((event) => {
      const at = new Date(event.at).getTime()
      if (event.type !== "courier") return [event, at]
      const start = anchors.filter((time) => time >= at && time - at <= 120000).sort((a, b) => b - a)[0]
      return [event, start !== undefined ? start + 1 : at]
    }))
    return all.sort((a, b) => (newestFirst ? when.get(b) - when.get(a) : when.get(a) - when.get(b)))
  })()
  // paid on delivery (and the project keeps sellers' invoices: api.invoice), and the buyer asked for an
  // invoice (or one is attached already): the invoice has a tab of its own
  const invoiceTab = order?.paymentMethod?.type === "cash_on_delivery" && !!api.invoice && (!!order?.invoiceDetails?.companyName || !!order?.sellerInvoice?.file)
  // asked for and not up yet: the tab says so
  const invoiceAwaited = invoiceTab && !!order?.invoiceDetails?.companyName && !order?.sellerInvoice?.file
  const tabIds = ["items", ...(shipsSomething ? ["delivery"] : []), ...(invoiceTab ? ["invoice"] : []), ...(requests.length ? ["requests"] : []), ...(order?.returns?.length ? ["returns"] : []), ...(order?.final?.refunds?.length ? ["refunds"] : []), "history", ...(order?.client ? ["client"] : [])]
  const shown = (section && (!order || tabIds.includes(section)) && section) || (openRequests ? "requests" : "items")
  // the courier asked now (api.checkTracking): a "check now", and the tracking or history part opened
  const [trackingBusy, setTrackingBusy] = useState(false)
  const checkTracking = async () => {
    if (!api.checkTracking || !order?.tracking?.number || trackingBusy) return
    setTrackingBusy(true)
    await api.checkTracking(id).catch(() => null)
    await load()
    setTrackingBusy(false)
  }
  useEffect(() => {
    if ((shown === "history" || (shown === "delivery" && deliveryShown === "tracking")) && order?.orderStatus === "shipping") checkTracking()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shown, deliveryShown, order?.tracking?.number])

  // what can be done to the order as it stands now — each asks first (Refund in its own dialog)
  const actions = useMemo(() => {
    if (!order) return []
    const list = []
    // given back in full — by a refund, after a return or a cancellation, whatever state it was
    // left in: nothing is left to send, finish, reopen or call off, though goods may still come back
    const refundedInFull = order.orderStatus === "refunded" || order.status === "refunded" || ((order.refundedAmount || 0) > 0 && (order.refundedAmount || 0) >= (order.amount || 0))
    if (refundedInFull) {
      if (order.returned?.returnable) list.push({ value: "returned", name: t`Returned`, hint: t`The goods came back: not collected, or sent back`, run: () => openReturn() })
      return list
    }
    // the buyer asked to call it off: nothing is sent or finished before the answer — Cancel accepts the request
    // (the banner's Decline keeps the order)
    if (openCancel) return [{ value: "cancel", name: t`Cancel the order`, hint: t`Accept the buyer's request and call the order off`, run: () => answerCancel(openCancel, "accepted") }]
    // not sent yet: paid, or placed to be paid on delivery (processing)
    const open = order.orderStatus === "paid" || order.orderStatus === "processing"
    const onDelivery = order.paymentMethod?.type === "cash_on_delivery"
    if (open && shipsSomething)
      list.push(atShop
        ? { value: "ship", name: t`Ready for pickup`, hint: onDelivery ? t`The order waits at your shop: the buyer pays when collecting it` : t`The order waits at your shop: mail the buyer`, run: ship }
        : { value: "ship", name: t`Ship`, hint: onDelivery ? t`Mark as on its way and mail the buyer — send it with cash on delivery` : t`Mark as on its way and mail the buyer`, run: ship })
    // a shipped, finished or called-off order goes back to being open — a
    // status set by hand can be taken back by hand (an order paid on delivery: back to processing)
    if (!open)
      list.push(onDelivery
        ? { value: "reopen", name: t`Awaiting shipment`, hint: t`Back to awaiting shipment: to be paid on delivery, not sent yet`, run: reopen }
        : { value: "reopen", name: t`Reopen`, hint: t`Back to open: waiting to be sent`, run: reopen })
    if (order.orderStatus !== "completed")
      list.push({ value: "complete", name: t`Complete`, hint: t`The buyer has it — the order is done`, run: complete })
    // not sent yet and nothing happened to it: its items can still change (the project's api has it)
    if (order.modifiable?.allowed && api.modify)
      list.push({ value: "modify", name: t`Modify order`, hint: t`Change quantities, take items out or add others — the buyer is told`, run: () => { setRefundHeight(panelHeight()); setModifying(true) } })
    // an Aldoo admin's: the order goes to another seller who sends it instead (the api says who may)
    if (order.movable?.allowed && api.move && !vendorView)
      list.push({ value: "move", name: t`Move to another seller`, hint: t`Another seller sends it instead — the buyer, the address and the delivery go over`, run: () => { setRefundHeight(panelHeight()); setMoving(true) } })
    if (order.orderStatus !== "canceled")
      list.push({ value: "cancel", name: t`Cancel`, hint: t`Call the order off; money is given back separately`, run: cancel })
    // goods came back — not collected, refused, or sent back in the return period; the money is
    // given back separately (the dialog offers the refund next)
    if (order.returned?.returnable)
      list.push({ value: "returned", name: t`Returned`, hint: t`The goods came back: not collected, or sent back`, run: () => openReturn() })
    if (refundable)
      list.push({ value: "refund", name: t`Refund`, hint: onDelivery ? t`Record money you gave the buyer back, wholly or by the line` : t`Give the card payment back, wholly or by the line`, run: () => openRefund() })
    // paid on delivery, completed (the buyer has paid) and no invoice on it yet: to the Invoice tab,
    // its drop area (the tab itself takes one at any state)
    if (invoiceTab && order.orderStatus === "completed" && !order.sellerInvoice?.file)
      list.push({ value: "attach_invoice", name: t`Attach invoice`, hint: invoiceAwaited ? t`The buyer asked for an invoice: add the PDF` : t`Add the invoice PDF of this order`, run: () => setSection("invoice") })
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, refundable, busy, t, invoiceTab, invoiceAwaited])

  // its items not out yet (pre-sale): the order waits for their release
  const presale = presaleOf(order)

  // where the sheet's sticky head ends in the scroll area (its height less the part held above
  // the top), so the Items table's column labels stick just under it; it grows with a banner or a chip
  const [head, setHead] = useState(null)
  const [headBottom, setHeadBottom] = useState(0)
  useEffect(() => {
    if (!head || typeof ResizeObserver === "undefined") return
    const measure = () => setHeadBottom(head.offsetHeight + (parseFloat(getComputedStyle(head).top) || 0))
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(head)
    window.addEventListener("resize", measure)
    return () => {
      observer.disconnect()
      window.removeEventListener("resize", measure)
    }
  }, [head])

  return (
    <Modal
      isOpen
      // closing the order lets its tab go from the url too
      onClose={() => {
        setSection("")
        onClose?.()
      }}
      footerContent={
        order ? (
      // the dialog's own footer: fixed under the sheet, which scrolls above it — never printed;
      // the print on the left, every way of changing the order under one control on the right
      <div className="flex flex-wrap items-center justify-between gap-2">
          {/* the order alone: Print prints it; with more to print (its waybill's label), a menu of them */}
          {prints.length > 1 ? (
            <div className="w-fit">
            <DropdownSelect
              variant="admin"
              placeholder={t`Print`}
              options={prints}
              menuPosition="top"
              // the menu's width only: the trigger stays as wide as the button
              dropdownWidth="260px"
              // the Print button as it is, with a chevron: the menu of what to print opens above it
              showChevron={false}
              customTrigger={
                <span className="h-[40px] flex items-center justify-center gap-1.5 border border-admin_primary text-admin_primary hover:text-white hover:bg-admin_primary font-bold rounded-full whitespace-nowrap py-2 px-4 transition-colors" data-testid="order-print">
                  <PrinterIcon className="w-4 h-4" />
                  {t`Print`}
                  <ChevronUpIcon className="w-4 h-4" />
                </span>
              }
              value={[]}
              showCheckbox={false}
              searchEnabled={false}
              enableResetButton={false}
              onItemRender={(option) => <ActionOption option={option} />}
              onChange={(picked) => {
                const option = Array.isArray(picked) ? picked[0] : picked
                prints.find((item) => item.value === option?.value)?.run()
              }}
              className="w-fit mr-auto"
              data-testid="order-print-menu"
            />
            </div>
          ) : (
            <Button variant="admin_secondary" onClick={() => window.print()}><PrinterIcon className="w-4 h-4 mr-1.5" />{t`Print`}</Button>
          )}
          {/* nothing can be done to it (refunded in full): no actions control at all */}
          {actions.length > 0 && <span className="inline-flex items-center gap-1.5 print:hidden">
          {/* sending, cancelling, returns and refunds, from the help center (helpKeys.ship) */}
          <HelpHint helpKey={helpKeys.ship} placement="top" align="end" />
          <DropdownSelect
            variant="admin"
            placeholder={t`Order actions`}
            options={actions}
            // at the foot of the sheet: the menu opens upward, every action
            // in view at once, wide enough for its line of explanation
            menuPosition="top"
            maxHeight="min(70vh, 480px)"
            fixedWidth="340px"
            value={[]}
            showCheckbox={false}
            searchEnabled={false}
            enableResetButton={false}
            disabled={busy}
            onItemRender={(option) => <ActionOption option={option} />}
            onChange={(picked) => {
              const option = Array.isArray(picked) ? picked[0] : picked
              actions.find((action) => action.value === option?.value)?.run()
            }}
            className="w-[220px]"
          />
          </span>}
        </div>
        ) : null
      }
      variant="admin_compact"
      styleOverrides={{
        // one height whatever tab is open, so the dialog never jumps: the
        // whole screen on a phone, 94vh on a desk — tall enough for a long
        // Items list — with the sheet scrolling inside it
        container:
          "order-dialog-panel bg-admin_canvas flex flex-col w-full h-[100dvh] max-h-[100dvh] md:max-w-[900px] md:h-[94vh] md:max-h-[94vh] overflow-hidden rounded-none md:rounded-md border-0 md:border border-admin_bordered shadow-lg print:h-auto print:max-h-none print:max-w-none print:border-0 print:shadow-none print:overflow-visible",
        // the dialog has no title of its own — the sheet names the order — so
        // the modal's header is only the strip the close button sits in, and
        // it is kept thin and out of the print
        header: "relative flex justify-end items-center px-3 h-8 shrink-0 print:hidden",
        closeButton: "absolute right-2 top-1.5 z-20",
        contentWrapper: "flex-1 min-h-0 overflow-y-auto scrollbar-thin scrollbar-thumb-admin_neutral_2 scrollbar-track-admin_paper print:overflow-visible",
        // the actions under the sheet: fixed at the foot, the sheet scrolling above them
        footer: "shrink-0 px-4 py-3 border-t border-admin_bordered bg-admin_canvas print:hidden",
        // the sheet fills what is left, so the actions stay at the foot of the dialog
        content: "p-0 min-h-full flex flex-col print:block",
      }}
    >
      {/* the print rules: the sheet alone, at its own size */}
      {/* the sheet sits in the dialog's fixed overlay, which a browser prints again on every page:
          in print the boxes around it are plain blocks, everything else on the page is left out,
          and the sheet runs from the top of the first page onto the next as long as it needs */}
      <style>{`@media print {
        html, body { height: auto !important; overflow: visible !important; background: #fff !important; }
        body *:not(:has(#order-sheet)):not(#order-sheet):not(#order-sheet *) { display: none !important; }
        *:has(#order-sheet) { position: static !important; transform: none !important; overflow: visible !important; height: auto !important; min-height: 0 !important; max-height: none !important; width: auto !important; max-width: none !important; margin: 0 !important; padding: 0 !important; border: 0 !important; box-shadow: none !important; background: transparent !important; display: block !important; }
        #order-sheet { position: static; margin: 0; width: auto; min-height: 0; padding: 0 16mm; box-shadow: none !important; border: 0 !important; }
        /* every page keeps its top and bottom margin, the one a long history runs onto too */
        @page { size: A4; margin: 12mm 0; }
        /* the thumbnails are part of the sheet: print them as they look */
        #order-sheet img { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      }`}</style>

      {!order && !error && (
        // in the middle of the dialog, as tall as the sheet it stands for
        <div className="flex-1 min-h-[60vh] flex items-center justify-center gap-3 p-10 text-admin_text_secondary">
          <Spinner variant="admin_primary" size="md" /> {t`Loading…`}
        </div>
      )}
      {error && <div className="p-6"><Empty>{error}</Empty></div>}

      {order && (
        <>
          {/* the sheet */}
          <div id="order-sheet" className="flex-1 bg-white text-black m-3 p-5 md:m-4 md:p-8 rounded-md shadow-sm print:flex-none print:m-0 print:rounded-none print:flex print:flex-col">
            {/* the order's name, its state and contacts, and its tabs: they stay at the top while the
                tab's content scrolls under them (the dialog's footer stays at the bottom the same way) */}
            <div ref={setHead} className="sticky -top-5 md:-top-8 z-20 bg-white -mx-5 md:-mx-8 px-5 md:px-8 -mt-5 md:-mt-8 pt-5 md:pt-8 rounded-t-md print:static print:m-0 print:p-0 print:order-0">
            <div className="flex items-start justify-between gap-6 border-b border-black/10 pb-4">
              <div>
                <div className="flex flex-wrap items-center gap-3">
                  {/* the number copies itself, for a mail or a courier form */}
                  <button
                    type="button"
                    onClick={copyId}
                    title={t`Copy order ID`}
                    className="group/copy inline-flex items-center gap-2 text-2xl font-semibold hover:text-admin_primary"
                    data-testid="order-copy-id"
                  >
                    {t("Order %s", id)}
                    <DocumentDuplicateIcon className="w-5 h-5 text-black/40 group-hover/copy:text-admin_primary print:hidden" aria-hidden />
                  </button>
                  {/* the sheet and its tabs, from the help center (helpKeys.order) */}
                  <HelpHint helpKey={helpKeys.order} className="print:hidden" iconClassName="w-5 h-5 text-black/50" />
                  {/* where the order stands, beside its number — one state,
                      the money having the last word */}
                  <Chip className={orderState(order).className}>{t(orderState(order).label)}</Chip>
                  {/* the goods and the money are two stories: a returned order refunded in
                      full says both, a partly returned one says so beside its state */}
                  {order.orderStatus === "returned" && final.state === "full" && <Chip className="bg-admin_error/10 text-admin_error">{t`refunded`}</Chip>}
                  {order.returned?.state === "partial" && <Chip className="bg-admin_warning/10 text-admin_warning">{t`partly returned`}</Chip>}
                  {openRequests > 0 && <Chip className="bg-admin_warning/10 text-admin_warning">{t`return requested`}</Chip>}
                  {openCancel && <Chip className="bg-admin_error/10 text-admin_error">{t`cancellation requested`}</Chip>}
                  {presale && <Chip className={PRESALE}>{t`Pre-order`}</Chip>}
                </div>
                {order.sellerName && <div className="text-sm font-medium mt-0.5">{order.sellerName}</div>}
                {/* the promo code is named where it is taken off, in the totals */}
                <div className="text-sm text-black/60 mt-1">{t("Placed %s", dateTime(placedAt(order), locale))}
                  <span className="print:hidden"> ({timeAgo(placedAt(order), t)})</span></div>
                {order.origin === "subscription" && <div className="text-sm text-black/60">{t(order.subscription?.renewal ? "Subscription renewal" : "Subscription payment")}</div>}
              </div>
              {/* who to reach about this order, as opposed to where it goes */}
              <div className="text-right text-sm">
                <div className="text-black/50 uppercase text-[11px] tracking-wide mb-1">{t`Customer contacts`}</div>
                {/* the buyer's name: their account's, else the one they gave with the address */}
                {/* the name and the red chip (returns, refunds) open the Client tab: the buyer across their orders */}
                {(customer.name || orderAddress?.name || order.client?.flagged) && (
                  <div className="flex items-center justify-end gap-2">
                    <ClientFlag client={order.client} t={t} onClick={() => setSection("client")} />
                    {(customer.name || orderAddress?.name) &&
                      (order.client ? (
                        <button type="button" onClick={() => setSection("client")} title={t`See the customer`} className="font-medium text-right hover:text-admin_primary" data-testid="order-customer-name">
                          {customer.name || orderAddress?.name}
                        </button>
                      ) : (
                        <div className="font-medium" data-testid="order-customer-name">{customer.name || orderAddress?.name}</div>
                      ))}
                  </div>
                )}
                {customer.email && <div className="text-black/60">{customer.email}</div>}
                {customer.phone && <div className="text-black/60">{customer.phone}</div>}
                {!customer.name && !orderAddress?.name && !customer.email && !customer.phone && <div className="text-black/60">—</div>}
              </div>
            </div>

            {/* how it stands with the money, in large: paid (by card), or to be paid on delivery —
                the courier collects it, so the parcel goes with the shipping provider's cash on delivery */}
            {order.paymentMethod?.type === "cash_on_delivery" && order.status === "pending" ? (
              <div className="my-3 flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="order-payment-cod">
                <BanknotesIcon className="w-9 h-9 shrink-0 text-amber-500" />
                <div className="flex flex-col gap-1">
                  {/* paid at the seller's own shop when collected (pickup from the shop), else to the courier */}
                  <div className="text-lg font-semibold text-black">{payAtShop ? t`Pay at the shop` : t`Cash on delivery`} · {money(order.amount, currency)}</div>
                  <div className="text-sm text-black/70">{payAtShop ? t`Not paid yet. The buyer pays when collecting it at your shop.` : t`Not paid yet. Send it with cash on delivery through your shipping provider.`}</div>
                </div>
              </div>
            ) : order.status === "completed" || order.status === "refunded" ? (
              <div className="my-3 flex items-center gap-3 rounded-lg border border-admin_success/30 bg-admin_success/5 p-4" data-testid="order-payment-paid">
                <CheckCircleIcon className="w-9 h-9 shrink-0 text-admin_success" />
                <div className="flex flex-col">
                  <div className="text-lg font-semibold text-black">{t`Paid`} · {money(order.amount, currency)}</div>
                  <div className="text-sm text-black/60">{payAtShop ? t`Paid at the shop` : order.paymentMethod?.type === "cash_on_delivery" ? t`Paid in cash on delivery` : t`Paid online at checkout`}</div>
                </div>
              </div>
            ) : null}

            {/* items not out yet (pre-sale): the order waits for them — when they are released, and the
                delivery the buyer was promised; printed too, for whoever packs it */}
            {presale && awaitingShipment(order) && (
              <div className="my-3 flex items-start gap-3 rounded-lg border border-[#7c3aed]/30 bg-[#7c3aed]/5 p-4 break-inside-avoid" data-testid="order-presale">
                <CalendarDaysIcon className="w-9 h-9 shrink-0 text-[#7c3aed]" />
                <div className="flex flex-col gap-1">
                  <div className="text-lg font-semibold text-black">{t`This order contains pre-order items`}</div>
                  <div className="text-sm text-black/70">
                    {t("Ship it once they are released, on %s.", dateOnly(presale.releaseDate, locale))}
                    {presale.deliverBy && <> {t("The buyer was promised delivery by %s.", dateOnly(presale.deliverBy, locale))}</>}
                  </div>
                </div>
              </div>
            )}

            {/* moved by an Aldoo admin to another seller (the admins see it): where it went, or came from */}
            {(order.movedTo?.orderId || order.movedTo?.byAldoo || (order.movedFrom?.orderId && !vendorView)) && (
              <div className="my-3 flex items-start gap-3 rounded-lg border border-admin_info/30 bg-admin_info/5 p-4 print:hidden" data-testid="order-moved">
                <ArrowsRightLeftIcon className="w-6 h-6 shrink-0 text-admin_info" />
                <div className="flex flex-col gap-0.5 text-sm text-black/80">
                  {/* the seller it was taken from: that Aldoo moved it, and why */}
                  {(order.movedTo?.byAldoo || (vendorView && order.movedTo)) && <span className="font-semibold text-black">{t`Aldoo moved this order to be fulfilled elsewhere: do not send it.`}</span>}
                  {order.movedTo?.reason && (
                    <span>
                      <span className="text-black/50">{t`Reason`}: </span>
                      <span className="whitespace-pre-line">{order.movedTo.reason}</span>
                    </span>
                  )}
                  {order.movedTo?.orderId && !vendorView && (
                    <span>
                      {t("Moved to %s as order", order.movedTo.sellerName || "")}{" "}
                      {onOpenOrder ? (
                        <button type="button" className="font-semibold text-admin_primary hover:underline" onClick={() => onOpenOrder(order.movedTo.orderId)}>
                          {order.movedTo.orderId}
                        </button>
                      ) : (
                        <span className="font-semibold">{order.movedTo.orderId}</span>
                      )}
                    </span>
                  )}
                  {order.movedFrom?.orderId && !vendorView && (
                    <span>
                      {t("Moved here from %s, order", order.movedFrom.sellerName || "")}{" "}
                      {onOpenOrder ? (
                        <button type="button" className="font-semibold text-admin_primary hover:underline" onClick={() => onOpenOrder(order.movedFrom.orderId)}>
                          {order.movedFrom.orderId}
                        </button>
                      ) : (
                        <span className="font-semibold">{order.movedFrom.orderId}</span>
                      )}
                    </span>
                  )}
                  {(order.movedTo || order.movedFrom)?.refunded > 0 && <span className="text-black/60">{t("%s went back to the buyer's card.", money((order.movedTo || order.movedFrom).refunded, order.currency))}</span>}
                  {(order.movedTo || order.movedFrom)?.covered > 0 && <span className="text-black/60">{t("Aldoo covers %s.", money((order.movedTo || order.movedFrom).covered, order.currency))}</span>}
                </div>
              </div>
            )}

            {/* the buyer asks to call the order off: what they said, and the two answers */}
            {openCancel && (
              <div className="my-3 rounded-lg border border-admin_error/30 bg-admin_error/5 p-4 flex flex-col gap-2 print:hidden" data-testid="cancel-request-banner">
                <div className="font-semibold text-black inline-flex items-center gap-1.5">{t`The buyer asks to cancel this order`}<HelpHint helpKey={helpKeys.cancel} /></div>
                <div className="text-sm text-black/70">
                  <span className="text-black/50">{t`Reason`}: </span>
                  {t(CANCEL_REASON_LABELS[openCancel.reason] || openCancel.reason)}
                  {openCancel.at && <span className="text-black/40"> · {dateTime(openCancel.at, locale, t)}</span>}
                </div>
                {openCancel.note && <div className="text-sm text-black/70 whitespace-pre-line">“{openCancel.note}”</div>}
                <div className="text-xs text-black/50">{t`Do not send it before you answer.`}</div>
                {api.resolveCancel && (
                  <div className="flex flex-wrap gap-2 justify-end">
                    <Button variant="admin_secondary" onClick={() => answerCancel(openCancel, "declined")} disabled={busy} data-testid="cancel-request-decline">
                      {t`Decline cancellation`}
                    </Button>
                    <Button variant="admin_primary" onClick={() => answerCancel(openCancel, "accepted")} disabled={busy} data-testid="cancel-request-accept">
                      {order.paymentMethod?.type === "cash_on_delivery" ? t`Cancel the order` : t`Cancel and refund`}
                    </Button>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-6 border-b border-black/10 mb-1 print:hidden" role="tablist">
              {[
                // the units left after the refunds (each line's quantity): what the order is now, its count in a chip
                ["items", <TabCount label={t`Items`} count={final.lines.reduce((n, line) => n + (line.quantity ?? 1), 0)} testId="order-items-count" />],
                ...(shipsSomething ? [["delivery", <span className="inline-flex items-center gap-1.5">{t`Delivery`}<HelpHint helpKey={helpKeys.ship} /></span>]] : []),
                // the seller's invoice of an order paid on delivery: upload it, see it (a dot while it is awaited)
                ...(invoiceTab ? [["invoice", <span className="inline-flex items-center gap-1.5">{t`Invoice`}{invoiceAwaited && <span className="w-2 h-2 rounded-full bg-admin_error" aria-label={t`awaiting invoice`} />}<HelpHint helpKey={helpKeys.invoice} /></span>]] : []),
                ...(requests.length ? [["requests", <span className="inline-flex items-center gap-1.5"><TabCount label={t`Return requests`} count={requests.length} /><HelpHint helpKey={helpKeys.returns} /></span>]] : []),
                ...(order.returns?.length ? [["returns", <TabCount label={t`Returns`} count={order.returns.length} />]] : []),
                ...(final.refunds.length ? [["refunds", <span className="inline-flex items-center gap-1.5"><TabCount label={t`Refunds`} count={final.refunds.length} /><HelpHint helpKey={helpKeys.refund} /></span>]] : []),
                // the order's life, event by event: the log of what happened, when, by whom
                ["history", t`History`],
                // the buyer across their orders; a sign on it when they returned goods or had money back
                ...(order.client
                  ? [["client", <span className="inline-flex items-center gap-1.5">{t`Client`}{order.client.flagged && <ExclamationTriangleIcon className="w-4 h-4 text-admin_error" aria-label={t`This customer has returns or refunds`} />}</span>]]
                  : []),
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={shown === id}
                  onClick={() => setSection(id)}
                  className={`-mb-px pb-2 pt-3 border-b-2 text-sm font-semibold ${shown === id ? "border-admin_primary text-black" : "border-transparent text-black/50 hover:text-black"}`}
                  data-testid={`order-section-${id}`}
                >
                  {label}
                </button>
              ))}
            </div>
            </div>

            {/* the delivery in three parts, down the side: the address (and how to reach the buyer), the waybill
                made here, and the parcel's tracking — the address is what is printed */}
            <div className={`${shown === "delivery" ? "flex" : "hidden"} ${shipsSomething ? "print:block" : ""} print:order-3 flex-col md:flex-row gap-4 md:gap-8 py-4 text-sm`} data-testid="order-section-delivery-body">
              <nav className="md:w-[180px] shrink-0 flex md:flex-col gap-1 print:hidden overflow-x-auto" aria-label={t`Delivery`} data-testid="order-delivery-tabs">
                {deliveryTabs.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setDeliveryTab(tab.id)}
                    aria-current={deliveryShown === tab.id ? "page" : undefined}
                    className={`text-left whitespace-nowrap px-3 py-2 rounded-md text-sm transition-colors ${deliveryShown === tab.id ? "bg-admin_primary/10 text-admin_primary font-semibold" : "text-black/70 hover:bg-black/5"}`}
                    data-testid={`order-delivery-tab-${tab.id}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
              <div className="flex-1 min-w-0">
                <div className={`${deliveryShown === "address" ? "grid" : "hidden"} print:grid grid-cols-1 sm:grid-cols-2 gap-8`} data-testid="order-delivery-address">
              <div>
                {/* one address: the checkout asks for one, and it is both
                    where the parcel goes and who is billed */}
                {/* a parcel for an office or a locker has no street: who
                    collects it, and the office below, are what the waybill needs */}
                <div className="text-black/50 uppercase text-[11px] tracking-wide mb-1">{pickupOffice ? t`Recipient` : t`Address`}</div>
                <div className="whitespace-pre-line" data-testid="order-recipient">
                  {pickupOffice ? orderAddress?.name || "—" : address(orderAddress) || "—"}
                </div>
                {/* how the courier (and the seller) reaches the buyer: each with its own label */}
                {customer.phone && (
                  <div className="mt-3" data-testid="order-recipient-phone">
                    <div className="text-black/50 uppercase text-[11px] tracking-wide mb-1">{t`Phone`}</div>
                    <a href={`tel:${customer.phone}`} className="hover:underline">{customer.phone}</a>
                  </div>
                )}
                {customer.email && (
                  <div className="mt-3" data-testid="order-recipient-email">
                    <div className="text-black/50 uppercase text-[11px] tracking-wide mb-1">{t`Email`}</div>
                    <a href={`mailto:${customer.email}`} className="hover:underline break-all">{customer.email}</a>
                  </div>
                )}
                {carrier && (
                  <div className="mt-3" data-testid="order-shipping-method">
                    <div className="text-black/50 uppercase text-[11px] tracking-wide mb-1">{t`Shipping method`}</div>
                    {/* the courier's logo (CMS ShippingCarrier, from the API as shippingProvider.carrier) */}
                    {provider?.carrier?.logo && <img src={cmsUrl(provider.carrier.logo)} alt="" className="inline-block h-6 w-auto max-w-[90px] object-contain align-middle mr-1.5" data-testid="order-carrier-logo" />}
                    {carrier}
                  </div>
                )}
                {pickupOffice && (
                  <div className="mt-2" data-testid="order-pickup-office">
                    <div className="text-black/50 uppercase text-[11px] tracking-wide mb-1">{pickupOffice.type === "shop" ? t`Collected at your shop` : pickupOffice.type === "locker" ? t`Deliver to locker` : t`Deliver to office`}</div>
                    {renderPickup ? renderPickup({ order, office: pickupOffice, provider, t, locale }) : <DefaultPickup office={pickupOffice} />}
                  </div>
                )}
              </div>
              {order.invoiceDetails?.companyName && (
                <div>
                  {/* the one case where the bill goes elsewhere */}
                  <div className="text-black/50 uppercase text-[11px] tracking-wide mb-1">{t`Invoice to`}</div>
                  <div className="whitespace-pre-line">
                    {[order.invoiceDetails.companyName, order.invoiceDetails.taxId && t("VAT %s", order.invoiceDetails.taxId), address(order.invoiceDetails.address)]
                      .filter(Boolean)
                      .join("\n")}
                  </div>
                </div>
              )}
                </div>
                {/* the waybill made with the courier here: its label, its cancellation (the project's) */}
                {deliveryShown === "waybill" && hasShipment && (
                  <div className="print:hidden" data-testid="order-delivery-waybill">
                    {renderShipment({ order, t, locale, onChanged: () => { setDeliveryTab("address"); load(); onChanged?.() } })}
                  </div>
                )}
                {/* the parcel once sent: its number, what the courier says, what happens next — not printed */}
                {deliveryShown === "tracking" && (
                  <div className="print:hidden" data-testid="order-delivery-tracking">
                    <TrackingPanel order={order} t={t} locale={locale} onEdit={api.setTracking ? editTracking : null} onShowHistory={() => setSection("history")} onCheck={api.checkTracking ? checkTracking : null} checking={trackingBusy} />
                  </div>
                )}
              </div>
            </div>

            {/* paid on delivery: the seller's own invoice — asked for or not, uploaded or awaited */}
            {shown === "invoice" && invoiceTab && (
              <div className="py-4 text-sm print:hidden" data-testid="order-section-invoice-body">
                <InvoicePanel order={order} api={api} t={t} locale={locale} onChanged={() => (load(), onChanged?.())} />
              </div>
            )}

            <table className={`${shown === "items" ? "table" : "hidden"} print:table print:order-1 w-full text-sm print:border-t border-black/10`} data-testid="order-section-items-body">
              {/* the column labels stay in sight while the lines scroll, just under the sheet's head */}
              <thead className="[&_th]:sticky [&_th]:z-10 [&_th]:bg-white print:[&_th]:static">
                <tr className="text-left text-[11px] uppercase tracking-wide text-black/50">
                  <th className="py-2" style={{ top: headBottom }}>{t`Item`}</th>
                  {/* whether the line travels: a parcel to send, or delivered on the spot */}
                  {shipsSomething && <th className="py-2 w-[90px] text-center" style={{ top: headBottom }}>{t`Delivery`}</th>}
                  <th className="py-2 w-[70px] text-right" style={{ top: headBottom }}>{t`Qty`}</th>
                  <th className="py-2 w-[110px] text-right" style={{ top: headBottom }}>{t`Price`}</th>
                  <th className="py-2 w-[110px] text-right" style={{ top: headBottom }}>{t`Total`}</th>
                </tr>
              </thead>
              <tbody>
                {/* what has to be sent first, what was delivered on the spot after */}
                {!final.lines.length && (
                  <tr className="border-t border-black/10">
                    <td className="py-4 text-center text-black/50 italic" colSpan={shipsSomething ? 5 : 4} data-testid="order-items-all-refunded">{t`Every item was refunded`}</td>
                  </tr>
                )}
                {[...final.lines].sort((a, b) => (a.isPhysical === false ? 1 : 0) - (b.isPhysical === false ? 1 : 0)).map((item, index) => {
                  // what of the line came back (returns): all of it is no longer the buyer's to have
                  const back = (order.returned?.lines || []).find((line) => line.cartIndex === item.cartIndex)
                  const returned = back?.returned || 0
                  const allBack = returned > 0 && back.left <= 0
                  return (
                  <tr key={`${item.pid}-${item.cartIndex ?? index}`} className={`border-t border-black/10 align-top ${allBack ? "text-black/50" : ""}`} data-testid="order-item" data-returned={returned || undefined}>
                    <td className="py-2">
                      <div className="flex items-start gap-3">
                        <Thumb src={item.thumbnail} name={item.name || item.pid} className="w-[50px] h-[50px] rounded shrink-0" />
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className={allBack ? "line-through" : ""}>{item.name || item.pid}</span>
                            {returned > 0 && (
                              <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[#6b5ca5]/15 text-[#6b5ca5] no-underline" data-testid="order-item-returned">
                                <ArrowUturnDownIcon className="w-3 h-3" aria-hidden />
                                {allBack ? t`Returned` : t("%s of %s returned", returned, back.bought)}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-black/50">{item.pid}{item.sku ? ` · ${item.sku}` : ""}</div>
                          {/* a line sold before it is out: when it is released */}
                          {item.presale?.releaseDate && (
                            <span className={`inline-flex items-center gap-1 mt-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${PRESALE}`} data-testid="order-item-presale">
                              <CalendarDaysIcon className="w-3 h-3" aria-hidden />
                              {t("Pre-order · released %s", dateOnly(item.presale.releaseDate, locale))}
                            </span>
                          )}
                          {lineDetails(item).map((detail, at) => (
                            <div key={at} className="text-xs text-black/70" data-testid="order-item-detail">{detail}</div>
                          ))}
                        </div>
                      </div>
                    </td>
                    {shipsSomething && (
                      <td className={`py-2 text-center font-semibold ${item.isPhysical === false ? "text-black/40" : "text-admin_text"}`} data-testid="order-item-delivery">
                        {item.isPhysical === false ? t`No` : allBack ? <span className="text-[#6b5ca5]">{t`Returned`}</span> : t`Yes`}
                      </td>
                    )}
                    <td className="py-2 text-right tabular-nums">
                      {item.quantity ?? 1}
                      {/* what the line was before some of it went back */}
                      {item.refundedQuantity > 0 && <div className="text-xs text-black/50 whitespace-nowrap">{t("%s refunded", item.refundedQuantity)}</div>}
                    </td>
                    <td className="py-2 text-right tabular-nums">
                      {money(item.price, currency)}
                      {item.originalPrice > (item.price ?? 0) && (
                        <div className="text-xs text-black/50">
                          <span className="line-through">{money(item.originalPrice, currency)}</span>
                          {item.discountPercent ? ` −${item.discountPercent}%` : ""}
                        </div>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums">{money((item.price ?? 0) * (item.quantity ?? 1), currency)}</td>
                  </tr>
                  )
                })}
              </tbody>
              <tfoot className="border-t border-black/10">
                {totals.lineDiscount > 0 && (
                  <>
                    <tr>
                      <td className="py-1.5 text-right text-black/60" colSpan={shipsSomething ? 4 : 3}>{t`Items before discount`}</td>
                      <td className="py-1.5 text-right tabular-nums">{money(totals.full, currency)}</td>
                    </tr>
                    <tr>
                      <td className="py-1.5 text-right text-black/60" colSpan={shipsSomething ? 4 : 3}>{t`Discount`}</td>
                      <td className="py-1.5 text-right tabular-nums">−{money(totals.lineDiscount, currency)}</td>
                    </tr>
                  </>
                )}
                <tr>
                  {/* the lines as bought: what went back is a row of its own, under Paid */}
                  <td className="py-1.5 text-right text-black/60" colSpan={shipsSomething ? 4 : 3}>{final.refunds.length ? t`Items ordered` : t`Items`}</td>
                  <td className="py-1.5 text-right tabular-nums">{money(totals.items, currency)}</td>
                </tr>
                {totals.promoDiscount > 0 && (
                  <tr>
                    <td className="py-1.5 text-right text-black/60" colSpan={shipsSomething ? 4 : 3}>
                      {order.promoCode ? t("Promo code %s", order.promoCode) : t`Promo code`}
                    </td>
                    <td className="py-1.5 text-right tabular-nums">−{money(totals.promoDiscount, currency)}</td>
                  </tr>
                )}
                {/* what the delivery cost, always said: "Free" is an answer
                    the buyer and the vendor both want on the sheet */}
                {(shipsSomething || totals.shipping > 0) && <tr>
                  <td className="py-1.5 text-right text-black/60" colSpan={shipsSomething ? 4 : 3}>{t("Shipping%s", carrier ? ` · ${carrier}` : "")}</td>
                  <td className="py-1.5 text-right tabular-nums">
                    {totals.shipping > 0 ? money(totals.shipping, currency) : t`Free`}
                  </td>
                </tr>}
                <tr className="font-semibold">
                  <td className="py-1.5 text-right" colSpan={shipsSomething ? 4 : 3}>{order.paymentMethod?.type === "cash_on_delivery" && order.status === "pending" ? (payAtShop ? t`To pay at the shop` : t`To collect on delivery`) : t`Paid`}</td>
                  <td className="py-1.5 text-right tabular-nums">{money(order.amount, currency)}</td>
                </tr>
                {/* what went back, as one sum: the refunds themselves are their own tab */}
                {final.refunds.length > 0 && (
                  <tr className="text-black/70" data-testid="order-refund-row">
                    <td className="py-1.5 text-right" colSpan={shipsSomething ? 4 : 3}>{t`Refunded`}</td>
                    <td className="py-1.5 text-right tabular-nums whitespace-nowrap">−{money(totals.refunded, currency)}</td>
                  </tr>
                )}
                {final.refunds.length > 0 && (
                  <tr className="font-semibold border-t border-black/10" data-testid="order-net-paid">
                    <td className="py-1.5 text-right" colSpan={shipsSomething ? 4 : 3}>{t("net_paid")}</td>
                    <td className="py-1.5 text-right tabular-nums">{money(totals.net, currency)}</td>
                  </tr>
                )}
              </tfoot>
            </table>

            {/* printed only: for the buyer in the parcel (not sent yet), or where the order stands (sent) */}
            {/* page 2 of the print: the details — where it goes, and the guide or the report — under the order's name */}
            <div className="hidden print:block print:order-2 print:break-before-page pb-2 border-b border-black/10" data-testid="print-details-heading">
              <div className="text-lg font-bold">{t("Order %s · details", id)}</div>
            </div>
            <div className="hidden print:block print:order-4">
              <PrintSections order={order} t={t} locale={locale} shipsSomething={shipsSomething} renderQR={renderQR} />
            </div>

            {/* the buyer's requests to send goods back, newest first: what, why, and the answer —
                an open one is answered here, an accepted one is taken in here when its parcel arrives */}
            {shown === "requests" && (
              <div className="flex flex-col gap-4 py-4 print:hidden" data-testid="order-section-requests-body">
                {requests.map((request, index) => (
                  <RequestThread
                    key={request._id}
                    request={request}
                    number={requests.length - index}
                    order={order}
                    cartLineOf={cartLineOf}
                    buyer={customer.name || customer.email}
                    buyerPerson={customer}
                    seller={order.seller || (order.sellerId ? { _id: order.sellerId, name: order.sellerName } : null)}
                    t={t}
                    locale={locale}
                    busy={busy}
                    onAnswer={answer}
                    // folded to its header; only a new one — waiting for the seller's answer — opens
                    defaultOpen={request.status === "open"}
                  />
                ))}
              </div>
            )}

            {shown === "client" && (
              <div className="print:hidden" data-testid="order-section-client-body">
                <ClientPanel
                  order={order}
                  api={api}
                  t={t}
                  locale={locale}
                  // another of their orders opens in this one's place, on its first tab
                  onOpenOrder={
                    onOpenOrder &&
                    ((other) => {
                      setSection("")
                      onOpenOrder(other)
                    })
                  }
                />
              </div>
            )}

            {shown === "history" && (
              <div className="print:hidden" data-testid="order-section-history-body">
                {/* the order's own events and what its courier said of the parcel, together, by time */}
                <OrderTimeline events={historyWithCourier} currency={currency} locale={locale} t={t} />
              </div>
            )}

            {/* every return of the order, one row each: when, why, what came back, and whether it went back on sale */}
            {shown === "returns" && (
              <table className="w-full text-sm print:hidden" data-testid="order-section-returns-body">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-black/50">
                    <th className="py-2 w-[150px]">{t`Date`}</th>
                    <th className="py-2">{t`Reason`}</th>
                    <th className="py-2">{t`What came back`}</th>
                  </tr>
                </thead>
                <tbody>
                  {(order.returns || []).map((ret, index) => (
                    <tr key={ret._id || index} className="border-t border-black/10 align-top" data-testid="order-return">
                      <td className="py-2">{ret.at ? dateTime(ret.at, locale, t) : "—"}</td>
                      <td className="py-2">
                        <div className="font-medium" data-testid="order-return-reason">{t(REASON_LABELS[ret.reason] || ret.reason)}</div>
                        {ret.note && <div className="text-xs text-black/60 mt-0.5">{ret.note}</div>}
                      </td>
                      <td className="py-2">
                        {(ret.lines || []).map((line, at) => (
                          <div key={at} className="flex items-center gap-2 mt-1 text-xs text-black/70">
                            <Thumb src={cartLineOf(line).thumbnail} name={line.name || line.pid} className="w-[36px] h-[36px] rounded shrink-0" />
                            <span>
                              {line.quantity} × {describeLine({ ...cartLineOf(line), ...line }, line.name || line.pid)}
                              {line.sku ? ` · ${line.sku}` : ""}
                            </span>
                          </div>
                        ))}
                        <div className="text-xs text-black/50 mt-0.5">{ret.restock ? t`Back on sale` : t`Kept out of stock`}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* every refund of the order, one row each: when, who, how, what, how much, why */}
            {shown === "refunds" && (
              <table className="w-full text-sm print:hidden" data-testid="order-section-refunds-body">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-black/50">
                    <th className="py-2 w-[150px]">{t`Date`}</th>
                    <th className="py-2">{t`Refund of`}</th>
                    <th className="py-2 w-[110px] text-right">{t`Amount`}</th>
                  </tr>
                </thead>
                <tbody>
                  {final.refunds.map((refund, index) => (
                    <tr key={refund.id || index} className="border-t border-black/10 align-top" data-testid="order-refund">
                      <td className="py-2">
                        <div>{refund.at ? dateTime(refund.at, locale, t) : "—"}</div>
                        <div className="text-xs text-black/50">{refund.external ? t`Made outside the store` : refund.byName || ""}</div>
                      </td>
                      <td className="py-2">
                        <div className="font-medium">{refundKind(refund)}</div>
                        {(refund.lines || []).map((line, at) =>
                          // the delivery fee given back: a line like the goods, a truck where the picture would be
                          line.kind === "shipping" ? (
                            <div key={at} className="flex items-center gap-2 mt-1 text-xs text-black/70" data-testid="order-refund-shipping">
                              <span className="w-[36px] h-[36px] rounded shrink-0 flex items-center justify-center bg-black/5">
                                <TruckIcon className="w-5 h-5" />
                              </span>
                              <span>
                                {t`Delivery`}
                                {line.amount !== null && line.amount !== undefined ? ` · ${money(line.amount, currency)}` : ""}
                              </span>
                            </div>
                          ) : (
                          <div key={at} className="flex items-center gap-2 mt-1 text-xs text-black/70" data-testid="order-refund-line">
                            <Thumb src={line.thumbnail} name={line.name || line.pid} className="w-[36px] h-[36px] rounded shrink-0" />
                            <span>
                              {line.quantity} × {describeLine(line, line.name || line.pid)}
                              {line.sku ? ` · ${line.sku}` : ""}
                              {line.price !== null && line.price !== undefined ? ` · ${t("%s each", money(line.price, currency))}` : ""}
                              {line.amount !== null && line.amount !== undefined ? ` · ${money(line.amount, currency)}` : ""}
                            </span>
                          </div>
                          )
                        )}
                        {/* the reason, when one was given */}
                        {refund.reason && <div className="text-xs text-black/60 mt-0.5">{refund.reason}</div>}
                      </td>
                      <td className="py-2 text-right tabular-nums whitespace-nowrap">−{money(refund.amount, currency)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-black/10">
                  <tr className="font-semibold">
                    <td className="py-1.5 text-right" colSpan={2}>{t`Refunded`}</td>
                    <td className="py-1.5 text-right tabular-nums">−{money(totals.refunded, currency)}</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </>
      )}

      {refunding && order && (
        <RefundDialog
          order={order}
          t={t}
          height={refundHeight}
          initial={refundInitial}
          onClose={async (done) => {
            setRefunding(false)
            setRefundInitial(null)
            if (!done) return
            await load()
            onChanged?.()
          }}
          onRefund={async ({ lines, amount, shipping, reason }) => {
            const res = await api.refund(id, { lines, amount, shipping, reason })
            if (res?.error) return res
            showNotification({ message: t`Refunded, the buyer was told`, className: "bg-admin_success text-white" })
            return res
          }}
        />
      )}
      {moving && order && (
        <MoveDialog
          order={order}
          t={t}
          api={api}
          height={refundHeight}
          renderOfficePicker={renderOfficePicker}
          onClose={async (done) => {
            setMoving(false)
            if (!done) return
            showNotification({ message: t("Moved: order %s with the new seller, the buyer was told", done.order_id), className: "bg-admin_success text-white" })
            await load()
            onChanged?.()
          }}
        />
      )}
      {modifying && order && (
        <ModifyDialog
          order={order}
          t={t}
          height={refundHeight}
          search={order.modifiable?.canAdd && api.modifyProducts ? (term) => api.modifyProducts(id, term) : null}
          onModify={(payload) => api.modify(id, payload)}
          onClose={async (done) => {
            setModifying(false)
            if (!done) return
            showNotification({ message: t`Order changed, the buyer was told`, className: "bg-admin_success text-white" })
            await load()
            onChanged?.()
          }}
        />
      )}
      {returning && order && (
        <ReturnDialog
          order={order}
          t={t}
          locale={locale}
          height={refundHeight}
          preset={returnPreset}
          onReturn={(payload) => api.markReturned(id, payload)}
          onClose={async (done) => {
            setReturning(false)
            setReturnPreset(null)
            if (!done) return
            showNotification({ message: t`Marked as returned`, className: "bg-admin_success text-white" })
            const res = await api.get(orderId)
            if (res?.order) setOrder(res.order)
            onChanged?.()
            // the money for what came back, at once: the refund opens with those goods picked
            if (done.refundNext && res?.order) openRefund(Object.fromEntries(done.lines.map((line) => [line.cartIndex, line.quantity])))
          }}
        />
      )}
      {/* the waybill made with the courier (the project's dialog) */}
      {shipping && order && renderShip && renderShip({
        order,
        t,
        locale,
        onClose: () => setShipping(false),
        onShipped: async () => {
          setShipping(false)
          await load()
          onChanged?.()
        },
        onManual: () => {
          setShipping(false)
          shipByNumber()
        },
      })}
    </Modal>
  )
}
